import { Box, Typography } from "@mui/material";
import { GAUGE_BAND_COLORS } from "../colors";
import { useDrillDownDialog } from "../DrillDownProvider";
import { gaugeBandsFromGoal } from "../gaugeBands";
import { DrillDownConfig, WidgetExplanationContent } from "../types";
import { WidgetCard } from "./WidgetCard";

// Gauge horizontal (réplica do Zoho "Treatments starting next 15 days"): barra com zonas vermelho/amarelo/
// verde derivadas do objetivo (amarelo em -20%, verde a partir do objetivo, super objetivo em +20%),
// preenchimento branco até ao valor e marcador do objetivo.
type HorizontalGaugeCardProps = {
    title: string;
    value: number | undefined;
    goal: number;
    explanation: WidgetExplanationContent;
    isLoading?: boolean;
    isError?: boolean;
    drillDown?: DrillDownConfig;
};

export const HorizontalGaugeCard = ({ title, value, goal, explanation, isLoading, isError, drillDown }: HorizontalGaugeCardProps) => {
    const drillDownDialog = useDrillDownDialog();
    const isClickable = Boolean(drillDown && drillDownDialog && !isLoading && !isError);

    const bands = gaugeBandsFromGoal(goal);
    const redWidthPct = (bands.yellowFrom / bands.max) * 100;
    const yellowWidthPct = ((bands.greenFrom - bands.yellowFrom) / bands.max) * 100;
    const greenWidthPct = ((bands.max - bands.greenFrom) / bands.max) * 100;
    const boundedValue = value === undefined ? 0 : Math.min(Math.max(value, 0), bands.max);
    const valueWidthPct = (boundedValue / bands.max) * 100;
    const goalLeftPct = (bands.greenFrom / bands.max) * 100;

    return (
        <WidgetCard title={title} explanation={explanation} bodyHeight={72} isLoading={isLoading} isError={isError} drillDown={drillDown}>
            <Box width="100%" sx={isClickable ? { cursor: "pointer" } : undefined}>
                <Box position="relative" height={18} mb={0.5}>
                    <Box position="absolute" left={`${goalLeftPct}%`} sx={{ transform: "translateX(-50%)" }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>
                            {bands.greenFrom}
                        </Typography>
                    </Box>
                </Box>
                <Box position="relative" height={40} borderRadius={1} overflow="hidden">
                    <Box display="flex" height="100%">
                        <Box width={`${redWidthPct}%`} bgcolor={GAUGE_BAND_COLORS.low} />
                        <Box width={`${yellowWidthPct}%`} bgcolor={GAUGE_BAND_COLORS.middle} />
                        <Box width={`${greenWidthPct}%`} bgcolor={GAUGE_BAND_COLORS.high} />
                    </Box>
                    <Box
                        position="absolute"
                        top="22%"
                        left={0}
                        height="56%"
                        width={`${valueWidthPct}%`}
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        sx={{ backgroundColor: "#fff", borderRadius: "0 2px 2px 0", pr: 1, minWidth: 24, boxShadow: "0 0 0 1px rgba(0,0,0,0.15)" }}
                    >
                        <Typography variant="body2" fontWeight={700} color="text.primary">
                            {value ?? "—"}
                        </Typography>
                    </Box>
                    <Box position="absolute" top={0} height="100%" left={`${goalLeftPct}%`} width="2px" bgcolor="#fff" sx={{ transform: "translateX(-50%)" }} />
                </Box>
                <Box display="flex" justifyContent="space-between" mt={0.5}>
                    <Typography variant="caption" color="text.secondary">
                        0
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {bands.max}
                    </Typography>
                </Box>
            </Box>
        </WidgetCard>
    );
};
